
import { useState, useCallback, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { compressImage } from '@/utils/imageCompression';
import { ImageFileData, MAX_FILES_FREE, MAX_FILES_PREMIUM, MAX_RETRY_ATTEMPTS, BackgroundRemovalResult } from '@/types/imageFile';
import { getUniqueFiles, limitFiles } from '@/utils/fileValidation';
import { 
  createImageFileData, 
  updateFileCompression, 
  setFileError, 
  setFileProcessing 
} from '@/utils/fileStateUtils';
import { useUsageTracking } from '@/hooks/useUsageTracking';
import { useCompressionHistory } from '@/hooks/useCompressionHistory';
import { removeBackgroundFromFile } from '@/utils/backgroundRemoval';
import { useAuth } from '@/hooks/useAuth';

export const useImageFiles = (isPremium: boolean = false) => {
  const [filesData, setFilesData] = useState<ImageFileData[]>([]);
  const { toast } = useToast();
  const maxFiles = isPremium ? MAX_FILES_PREMIUM : MAX_FILES_FREE;
  const { saveCompressionHistory } = useCompressionHistory();
  const { user } = useAuth();
  const compressionResults = useRef<{
    originalSize: number;
    compressedSize: number;
    success: boolean;
  }[]>([]);
  
  const {
    canProcessFiles,
    incrementProcessedFiles,
    getRemainingFiles: getUsageRemainingFiles,
    hasReachedLimit,
    dailyUsage,
    freeLimit,
    trackUsage,
    requiresRegistration,
    showRegistrationModal,
    setShowRegistrationModal,
    anonymousLimit
  } = useUsageTracking(isPremium, user);

  const addFiles = useCallback(async (newFiles: File[]) => {
    const uniqueFiles = getUniqueFiles(newFiles, filesData.map(fd => fd.file));
    
    // Para usuarios premium, no limitar archivos (ilimitado)
    let filesToAdd: File[];
    if (isPremium) {
      filesToAdd = uniqueFiles;
    } else {
      const availableSlots = maxFiles - filesData.length;
      filesToAdd = limitFiles(uniqueFiles, availableSlots);
    }
    
    const newFilesData = filesToAdd.map(createImageFileData);
    
    setFilesData(prevFiles => [...prevFiles, ...newFilesData]);
  }, [filesData.length, maxFiles, isPremium]);

  const compressFile = useCallback(async (index: number, compressionConfig: any, resultIndex?: number) => {
    const fileData = filesData[index];
    if (!fileData || fileData.isCompressing) return false;

    // Verificar límite de uso para usuarios no premium
    if (!isPremium && !canProcessFiles(1)) {
      toast({
        title: "Límite diario alcanzado",
        description: `Has alcanzado tu límite de ${freeLimit} compresiones gratuitas por día. Actualiza a Premium para comprimir imágenes ilimitadas.`,
        variant: "destructive",
      });
      return false;
    }

    setFilesData(prevFiles => setFileProcessing(prevFiles, index));

    try {
      console.log('🔄 Comprimiendo archivo:', fileData.file.name);
      const result = await compressImage(fileData.file, compressionConfig.quality, {
        autoResize: compressionConfig.autoResize,
        maxWidth: compressionConfig.maxWidth,
        maxHeight: compressionConfig.maxHeight
      });
      
      console.log('✅ Compresión exitosa para:', fileData.file.name);
      setFilesData(prevFiles => updateFileCompression(prevFiles, index, result));
      
      // Update compression results tracking if provided
      if (typeof resultIndex === 'number' && compressionResults.current[resultIndex]) {
        compressionResults.current[resultIndex].compressedSize = result.compressedSize;
        compressionResults.current[resultIndex].success = true;
      }
      
      // Incrementar contador de uso para usuarios no premium
      if (!isPremium) {
        incrementProcessedFiles(1);
      }
      
      toast({
        title: "Imagen comprimida",
        description: `${fileData.file.name} comprimida exitosamente`,
      });
      
      return true;
      
    } catch (error) {
      console.error('❌ Error comprimiendo archivo:', fileData.file.name, error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido al comprimir la imagen';
      setFilesData(prevFiles => setFileError(prevFiles, index, errorMessage));
      
      toast({
        title: "Error de compresión",
        description: `Error en ${fileData.file.name}: ${errorMessage}`,
        variant: "destructive",
      });
      
      return false;
    }
  }, [filesData, toast, isPremium, canProcessFiles, incrementProcessedFiles, freeLimit]);

  const compressAllFiles = useCallback(async (compressionConfig: any) => {
    const pendingFiles = filesData
      .map((fileData, index) => ({ fileData, index }))
      .filter(({ fileData }) => fileData.status === 'pending');

    // Verificar límite de uso para usuarios no premium
    if (!isPremium && !canProcessFiles(pendingFiles.length)) {
      const remainingUsage = getUsageRemainingFiles();
      toast({
        title: "Límite de compresión",
        description: `Solo puedes comprimir ${remainingUsage} imagen(es) más hoy. Actualiza a Premium para comprimir imágenes ilimitadas.`,
        variant: "destructive",
      });
      return;
    }

    // Reset compression results tracking
    compressionResults.current = [];

    // Add all pending files to tracking
    pendingFiles.forEach(({ fileData }) => {
      compressionResults.current.push({
        originalSize: fileData.file.size,
        compressedSize: 0,
        success: false
      });
    });

    // Compress files one by one
    let completedIndex = 0;
    for (const { index } of pendingFiles) {
      // Verificar límite antes de cada compresión
      if (!isPremium && !canProcessFiles(1)) {
        toast({
          title: "Límite diario alcanzado",
          description: "Has alcanzado tu límite de compresiones gratuitas por día.",
          variant: "destructive",
        });
        break;
      }
      
      const success = await compressFile(index, compressionConfig, completedIndex);
      completedIndex++;
      
      if (!success) {
        break;
      }
    }

    // Save compression history after all files are processed
    const successfulCompressions = compressionResults.current.filter(r => r.success);
    if (successfulCompressions.length > 0) {
      const totalOriginalSize = successfulCompressions.reduce((sum, r) => sum + r.originalSize, 0);
      const totalCompressedSize = successfulCompressions.reduce((sum, r) => sum + r.compressedSize, 0);
      
      // Track analytics via Edge Function
      trackUsage(successfulCompressions.length, totalOriginalSize, totalCompressedSize);
      
      try {
        await saveCompressionHistory({
          totalImages: successfulCompressions.length,
          originalSizeBytes: totalOriginalSize,
          compressedSizeBytes: totalCompressedSize,
          qualitySetting: compressionConfig.quality,
          presetUsed: compressionConfig.preset,
          autoResizeEnabled: compressionConfig.autoResize || false,
          maxWidth: compressionConfig.maxWidth,
          maxHeight: compressionConfig.maxHeight,
        });
        
        toast({
          title: "Historial actualizado",
          description: `Se guardaron ${successfulCompressions.length} compresiones en el historial`,
        });
      } catch (error) {
        console.error('Error saving compression history:', error);
      }
    }
  }, [filesData, compressFile, isPremium, canProcessFiles, getUsageRemainingFiles, toast, saveCompressionHistory, trackUsage]);

  const removeFile = useCallback((index: number) => {
    setFilesData(prevFiles => {
      const fileData = prevFiles[index];
      if (fileData?.compression?.compressedUrl) {
        URL.revokeObjectURL(fileData.compression.compressedUrl);
      }
      return prevFiles.filter((_, i) => i !== index);
    });
  }, []);

  const clearFiles = useCallback(() => {
    setFilesData(prevFiles => {
      prevFiles.forEach(fileData => {
        if (fileData.compression?.compressedUrl) {
          URL.revokeObjectURL(fileData.compression.compressedUrl);
        }
      });
      return [];
    });
  }, []);

  const retryFile = useCallback(async (index: number, compressionConfig: any) => {
    const fileData = filesData[index];
    if (!fileData || fileData.retryCount >= MAX_RETRY_ATTEMPTS) return;

    await compressFile(index, compressionConfig);
  }, [filesData, compressFile]);

  const getCompletedFiles = useCallback(() => {
    return filesData.filter(fileData => fileData.status === 'completed');
  }, [filesData]);

  const getFailedFiles = useCallback(() => {
    return filesData.filter(fileData => fileData.status === 'failed');
  }, [filesData]);

  const canAddFiles = useCallback((count: number) => {
    if (isPremium) return true; // Usuarios premium pueden agregar archivos ilimitados
    return filesData.length + count <= maxFiles;
  }, [filesData.length, maxFiles, isPremium]);

  const getRemainingSlots = useCallback(() => {
    if (isPremium) return Infinity; // Usuarios premium tienen slots ilimitados
    return maxFiles - filesData.length;
  }, [filesData.length, maxFiles, isPremium]);

  const removeBackgroundFile = useCallback(async (index: number) => {
    const fileData = filesData[index];
    if (!fileData || fileData.isRemovingBackground) return false;

    // Actualizar estado a "procesando"
    setFilesData(prevFiles => 
      prevFiles.map((fd, i) => 
        i === index ? { ...fd, isRemovingBackground: true, error: undefined } : fd
      )
    );

    try {
      console.log('🎨 Removiendo fondo de:', fileData.file.name);
      
      const result = await removeBackgroundFromFile(fileData.file, (stage) => {
        console.log('📍 Progreso:', stage);
      });
      
      console.log('✅ Fondo removido exitosamente para:', fileData.file.name);
      
      setFilesData(prevFiles =>
        prevFiles.map((fd, i) =>
          i === index
            ? {
                ...fd,
                backgroundRemoval: result,
                isRemovingBackground: false,
                error: undefined,
              }
            : fd
        )
      );
      
      toast({
        title: "Fondo removido",
        description: `${fileData.file.name} procesada exitosamente - Lista para sublimación`,
      });
      
      return true;
      
    } catch (error) {
      console.error('❌ Error removiendo fondo:', fileData.file.name, error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido al remover el fondo';
      
      setFilesData(prevFiles =>
        prevFiles.map((fd, i) =>
          i === index
            ? {
                ...fd,
                isRemovingBackground: false,
                error: errorMessage,
              }
            : fd
        )
      );
      
      toast({
        title: "Error al remover fondo",
        description: `Error en ${fileData.file.name}: ${errorMessage}`,
        variant: "destructive",
      });
      
      return false;
    }
  }, [filesData, toast]);

  const removeBackgroundAllFiles = useCallback(async () => {
    const pendingFiles = filesData
      .map((fileData, index) => ({ fileData, index }))
      .filter(({ fileData }) => !fileData.backgroundRemoval && !fileData.isRemovingBackground);

    for (const { index } of pendingFiles) {
      await removeBackgroundFile(index);
    }
  }, [filesData, removeBackgroundFile]);

  return {
    filesData,
    files: filesData.map(fd => fd.file),
    addFiles,
    removeFile,
    clearFiles,
    compressFile,
    compressAllFiles,
    retryFile,
    removeBackgroundFile,
    removeBackgroundAllFiles,
    getCompletedFiles,
    getFailedFiles,
    totalFiles: filesData.length,
    canAddFiles,
    getRemainingSlots,
    maxFiles: isPremium ? 'Ilimitado' : maxFiles,
    maxRetryAttempts: MAX_RETRY_ATTEMPTS,
    // Datos de uso para usuarios no premium
    usageData: {
      canProcessFiles,
      remainingFiles: getUsageRemainingFiles(),
      hasReachedLimit,
      dailyUsage,
      freeLimit,
      requiresRegistration,
      showRegistrationModal,
      setShowRegistrationModal,
      anonymousLimit,
      trackUsage
    }
  };
};
